import { apiGet, APIURL, appendFlatpickrBaseStyles, cancelSelectEvent, changeSymbolForWidget, getCSSStyles, getHeaderPinButtonHTML, getHeaderPinStateClass, getNoDataHTML, getSymbolDropdown, getSymbolOptions, getTraderSMCardHTML, getWidgetDataURI, isHeaderPinned, normalizeWidgetTheme, setupHeaderPin, toggleWidgetTheme } from '../../helpers';
  import {
    WidgetConfig,
    WidgetEvent,
    WidgetEventHandler,
    WidgetEventType,
  } from '../../types';
  import flatpickr from "flatpickr";
  import { format } from "date-fns";
  
  export class TraderverseCryptoCoinDetail {
    private container: HTMLElement;
    private config: WidgetConfig;
    private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
    private updateInterval: number | null = null;
    private shadowRoot: ShadowRoot;
    private currentData: any = null;
    private widgetResizeObserver: ResizeObserver | null = null;
  
    constructor(config: WidgetConfig) {
      this.config = { ...this.getDefaultConfig(), ...config };
      this.config.theme = normalizeWidgetTheme(this.config.theme);
      const cryptoList = getSymbolOptions("crypto");
      const defaultEntry = cryptoList[0];
      const sym = (this.config.symbol || "").trim().toUpperCase();
      const match = cryptoList.find((c) => c.symbol === sym);
      if (match) {
        this.config.symbol = match.symbol;
        this.config.stock = { ...match };
      } else {
        this.config.symbol = defaultEntry.symbol;
        this.config.stock = { ...defaultEntry };
      }

      this.container = this.resolveContainer(config.container);
  
      if ((this.container as any).shadowRoot) {
        this.shadowRoot = (this.container as any).shadowRoot;
      } else {
        this.shadowRoot = this.container.attachShadow({ mode: "closed" });
      }
      this.init();
  
      this.widgetResizeObserver = new ResizeObserver(() => {
        this.updateDisplay();
      });
      setTimeout(() => {
        const widget = this.shadowRoot.querySelector(".widgetBox") as HTMLElement;
        if (widget) this.widgetResizeObserver?.observe(widget);
      }, 0);
    }
  
    private fetchSymbols(): Array<{ value: string; label: string }> {
      return [
        { label: "BTC", value: "BTC" },
        { label: "MSFT", value: "MSFT" },
        { label: "GOOGL", value: "GOOGL" },
        { label: "AMZN", value: "AMZN" },
        { label: "NVDA", value: "NVDA" },
        { label: "META", value: "META" },
        { label: "TSLA", value: "TSLA" },
        { label: "AVGO", value: "AVGO" },
        { label: "PEP", value: "PEP" },
        { label: "COST", value: "COST" },
      ];
    }
  
    private getDefaultConfig(): Partial<WidgetConfig> {
      const symbols = this.fetchSymbols();
      return {
      headerPinned: true,
        symbol: symbols[0].value,
        theme: "default-dark",
        width: "100%",
        height: "500px",
        autosize: true,
      };
    }
  
    private resolveContainer(container: string | HTMLElement): HTMLElement {
      if (typeof container === "string") {
        const el = document.getElementById(container) || document.querySelector(container);
        if (!el) throw new Error(`Container not found: ${container}`);
        return el as HTMLElement;
      }
      return container;
    }
  
    private async init(): Promise<void> {
      if (!this.config.symbol) this.config.symbol = this.fetchSymbols()[0].value;
  
      this.createWidget();
      const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
      if (widget) widget.classList.add('loading');
      this.attachStyles();
      await this.setupChart(this.config.symbol!);
      if (widget) widget.classList.remove('loading');
      this.emit("ready", {});
      this.config.onReady?.();
      cancelSelectEvent(this.shadowRoot);
    }
  
    private createWidget(): void {
  
      let themeClass = 'defaultDarkMode';
  let dataTheme = 'default-dark';
  
  if (this.config.theme === 'white') {
    themeClass = 'whiteMode';
    dataTheme = 'white';
  } else if (this.config.theme === 'black') {
    themeClass = 'blackMode';
    dataTheme = 'black';
  } else if (this.config.theme === 'default-lite') {
  themeClass = 'defaultLightMode';
  dataTheme = 'default-lite';
}
      const widgetHtml = `
        <div style="width: ${typeof this.config.width == "number" ? this.config.width+ 'px' : this.config.width  };height: ${typeof this.config.height == "number" ? this.config.height+ 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${this.config.theme}">
          <div class="headTop">
            <h4 class="title crypto">${this.getTitleIconSvg()} Coin Detail</h4>
            <div class="actionCol">${getHeaderPinButtonHTML(this.config)}<i class="pi pi-ellipsis-v"></i></div>
          </div>
  
          <div class="inBody">
            <div class="optionsRow">
              ${this.config.hideSymbol ? '<div class="item dropStyle01"></div>' : `<div class="item dropStyle01">
                <div class="trader-sm-card" id="selected-symbol">
                  ${getTraderSMCardHTML(this.config.stock ?? {
        symbol: this.config.symbol!,
        type: "crypto",
        name: "",
      })}
                </div>
                <span id="symbol-dropdown-placeholder"></span>
              </div>`}
            </div>
            <div class="chartDv" id="eventsContainer" style="height: 100%; padding: 0; margin: 0;"></div>
          </div>
        </div>
      `;
  
      this.shadowRoot.innerHTML = widgetHtml;
    setupHeaderPin(this.shadowRoot, this.config);
  
      if (this.config.isSymbolSelectable && !this.config.hideSymbol) {
        const dropdown = getSymbolDropdown((data: any) => this.onSelectChange(data), 'crypto');
        const placeholder = this.shadowRoot.getElementById("symbol-dropdown-placeholder");
        if (placeholder && dropdown) placeholder.replaceWith(dropdown);
      }
  
      this.setupChart(this.config.symbol!);
    }
  
    onSelectChange(stock: any) {
      this.changeSymbol(stock.symbol, stock);
    }
  
    private changeSymbol(stockId: string, stock: any = null): void {
      changeSymbolForWidget(
        this.shadowRoot,
        this.config,
        stock,
        stockId,
        (stockId: any) => this.setupChart(stockId),
        (type: any, data: any) => this.emit(type, data)
      );
    }
  
    private async attachStyles(): Promise<void> {
      try {
        appendFlatpickrBaseStyles(this.shadowRoot);
        const styleEl = await getCSSStyles();
        this.shadowRoot.appendChild(styleEl);
  
        const customStyle = document.createElement("style");
        customStyle.textContent = `
          
        `;
        this.shadowRoot.appendChild(customStyle);
      } catch (err) {
        console.error("Failed to load CSS:", err);
      }
    }
  
    private async setupChart(symbol: string): Promise<void> {
      try {
        const response = await apiGet({
          method: "POST",
          url: `${APIURL}/v1/crypto/about`,
          body: { symbol },
        });
  
        if (!response.ok) throw new Error("Failed to fetch data");
        this.currentData = await response.json();
  
        this.updateDisplay();
        this.emit("dataUpdate", this.currentData);
      } catch (err) {
        console.error("Error fetching chart data:", err);
      }
    }
  
    /** Resolves API fields that may be a plain string or a locale map like `{ en: "..." }`. */
    private resolveLocalizedText(value: unknown): string {
      if (value == null) return "";
      if (typeof value === "string") return value;
      if (typeof value === "object" && !Array.isArray(value)) {
        const dict = value as Record<string, string>;
        const primary =
          typeof navigator !== "undefined" && navigator.language
            ? navigator.language.split("-")[0]!.toLowerCase()
            : "en";
        if (typeof dict[primary] === "string" && dict[primary]) return dict[primary]!;
        if (typeof dict.en === "string" && dict.en) return dict.en;
        const first = Object.values(dict).find((v) => typeof v === "string" && v.length > 0);
        return first ?? "";
      }
      return "";
    }
  
    private renderCompanyDetails(data: any): void {
      const container = this.shadowRoot.querySelector("#eventsContainer");
      if (!container) return;
      const info = data?.data;
const hasData =
  !!info &&
  (
    this.resolveLocalizedText(info.description) ||
    info.Address ||
    info.CountryName ||
    info.Officers?.length
  );

// 👉 HERE IS THE FIX
if (hasData) {
  container.setAttribute("data-testing-id", "coin-events-container");
} else {
  container.removeAttribute("data-testing-id");
}
      if (!info) {
        container.innerHTML = getNoDataHTML();
        return;
      }
  
      const cryptoName = typeof info.name === "string" ? info.name.trim() : "";
      const description =
        this.resolveLocalizedText(info.description) ||
        this.resolveLocalizedText(info.Description) ||
        (info.BusinessSummary ?? info.LongBusinessDescription ?? "");
  
      const ceo = info.Officers?.find((o: any) =>
        o.Title?.toLowerCase().includes("ceo")
      );
  
      const detailRows: { label: string; value: string }[] = [];
  
      const maybePush = (label: string, display: string | undefined | null) => {
        if (display != null && String(display).trim() !== "") {
          detailRows.push({ label, value: String(display) });
        }
      };
  
      maybePush("Headquarters", info.Address);
      maybePush("Country", info.CountryName);
      if (ceo?.Name) maybePush("CEO", ceo.Name);
      maybePush("Sector", info.Sector);
      maybePush("Industry", info.Industry);
      if (info.FullTimeEmployees != null && info.FullTimeEmployees !== "") {
        maybePush(
          "Employees",
          typeof info.FullTimeEmployees === "number"
            ? info.FullTimeEmployees.toLocaleString()
            : String(info.FullTimeEmployees)
        );
      }
      if (info.WebURL) {
        detailRows.push({
          label: "Website",
          value: `<a href="${info.WebURL}" target="_blank">${info.WebURL}</a>`,
        });
      }
      maybePush("IPO Year", info.IPODate);
      maybePush("Exchange", info.Exchange);
  
      if (!cryptoName && !description && detailRows.length === 0) {
        container.innerHTML = getNoDataHTML();
        return;
      }
  
let html = `
  <div class="companyStockDetailsGrid"
`;
  
      if (description) {
        html += `
          <div style="grid-column: 1 / -1;">
            <div class="detailItem">
              <p class="title">Description</p>
              <p class="para" style="white-space: pre-line;">${description}</p>
            </div>
          </div>
        `;
      }
  
      for (const row of detailRows) {
        html += `
          <div class="detailItem">
            <p class="title">${row.label}</p>
            <p class="para">${row.value}</p>
          </div>
        `;
      }
  
      html += `</div>`;
      container.innerHTML = html;
    }
  
  
    private updateDisplay(): void {
      if (!this.currentData) return;
      this.renderCompanyDetails(this.currentData);
    }
  
    public on(type: WidgetEventType, handler: WidgetEventHandler): void {
      if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
      this.eventHandlers.get(type)!.push(handler);
    }
  
    public off(type: WidgetEventType, handler: WidgetEventHandler): void {
      const list = this.eventHandlers.get(type);
      if (list) this.eventHandlers.set(type, list.filter((h) => h !== handler));
    }
  
    public setSymbol(symbol: string): void {
      this.changeSymbol(symbol);
      const dropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
      if (dropdown) dropdown.value = symbol;
    }
  
    public async getWidgetImage(): Promise<string> {
      const widget = this.shadowRoot.querySelector(".widgetBox") as HTMLElement;
      return await getWidgetDataURI(widget);
    }
  
  public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
    if (!this.shadowRoot) return;
  
    this.config.theme = toggleWidgetTheme(
      this.shadowRoot,
      theme
    ) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';
  
    this.updateDisplay();
  }
  
    public destroy(): void {
      if (this.updateInterval) clearInterval(this.updateInterval);
      if (this.widgetResizeObserver) this.widgetResizeObserver.disconnect();
      this.eventHandlers.clear();
      this.shadowRoot.innerHTML = "";
    }
  
    private emit(type: WidgetEventType, data: any): void {
      const handlers = this.eventHandlers.get(type);
      if (handlers) {
        const evt: WidgetEvent = { type, data };
        handlers.forEach((h) => h(evt));
      }
    }
    private getTitleIconSvg(): string {
      return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M8.59998 23.625C8.08748 23.625 7.66248 23.2 7.66248 22.6875V20.1C7.66248 19.5875 8.08748 19.1625 8.59998 19.1625C9.11248 19.1625 9.53748 19.5875 9.53748 20.1V22.6875C9.53748 23.2125 9.11248 23.625 8.59998 23.625Z" fill="currentColor"/> <path d="M15 23.625C14.4875 23.625 14.0625 23.2 14.0625 22.6875V17.5C14.0625 16.9875 14.4875 16.5625 15 16.5625C15.5125 16.5625 15.9375 16.9875 15.9375 17.5V22.6875C15.9375 23.2125 15.5125 23.625 15 23.625Z" fill="currentColor"/> <path d="M21.4 23.625C20.8875 23.625 20.4625 23.2 20.4625 22.6875V14.9125C20.4625 14.4 20.8875 13.975 21.4 13.975C21.9125 13.975 22.3375 14.4 22.3375 14.9125V22.6875C22.3375 23.2125 21.925 23.625 21.4 23.625Z" fill="currentColor"/> <path d="M8.59997 16.475C8.17497 16.475 7.79997 16.1875 7.68747 15.7625C7.56247 15.2625 7.86247 14.75 8.37497 14.625C12.975 13.475 17.025 10.9625 20.1125 7.37502L20.6875 6.70002C21.025 6.31252 21.6125 6.26252 22.0125 6.60002C22.4 6.93752 22.45 7.52502 22.1125 7.92502L21.5375 8.60002C18.2 12.5 13.8 15.2125 8.82497 16.45C8.74997 16.475 8.67497 16.475 8.59997 16.475Z" fill="currentColor"/> <path d="M21.4001 11.9C20.8876 11.9 20.4626 11.475 20.4626 10.9625V8.25H17.7375C17.225 8.25 16.8 7.825 16.8 7.3125C16.8 6.8 17.225 6.375 17.7375 6.375H21.4001C21.9126 6.375 22.3376 6.8 22.3376 7.3125V10.975C22.3376 11.4875 21.9251 11.9 21.4001 11.9Z" fill="currentColor"/> <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/> </svg>`;
    }
  
  }
  
  declare global {
    interface Window {
      TraderverseCryptoCoinDetail: typeof TraderverseCryptoCoinDetail;
    }
  }
  if (typeof window !== "undefined") {
window.TraderverseCryptoCoinDetail = TraderverseCryptoCoinDetail;
  }
  